<script setup>
import { computed } from 'vue';
import UserSearchInput from '@/js/core/components/parts/trays/UserSearchInput.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  inputId: {
    type: String,
    required: true,
  },
  searchLabel: {
    type: String,
    default: 'Add a user',
  },
  excludedUuids: {
    type: Array,
    default: () => [],
  },
  roleUsers: {
    type: Array,
    default: () => [],
  },
  error: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const selectedUuids = computed(() => props.modelValue.map(user => user.uuid));

const roleOnlyUsers = computed(() =>
  props.roleUsers.filter(user => !selectedUuids.value.includes(user.uuid))
);

const excludedUuids = computed(() => [
  ...props.excludedUuids,
  ...selectedUuids.value,
  ...props.roleUsers.map(user => user.uuid),
]);

const handleAddUser = user => {
  if (selectedUuids.value.includes(user.uuid)) {
    return;
  }

  emit('update:modelValue', [...props.modelValue, user]);
};

const handleRemoveUser = userUuid => {
  emit(
    'update:modelValue',
    props.modelValue.filter(user => user.uuid !== userUuid)
  );
};
</script>

<template>
  <div class="tray-user-selector">
    <UserSearchInput
      :input-id="inputId"
      :label="searchLabel"
      :excluded-uuids="excludedUuids"
      :error="error"
      @select="handleAddUser"
    />

    <ul v-if="modelValue.length || roleOnlyUsers.length" class="tray-user-selector__selected-list">
      <li
        v-for="user in roleOnlyUsers"
        :key="`role-${user.uuid}`"
        class="tray-user-selector__selected-item"
      >
        <span class="tray-user-selector__user">
          <span class="tray-user-selector__name"
            >{{ user.name }} <span class="tray-user-selector__role-label">(Manager)</span></span
          >
        </span>
      </li>
      <li v-for="user in modelValue" :key="user.uuid" class="tray-user-selector__selected-item">
        <span class="tray-user-selector__user">
          <span class="tray-user-selector__name">{{ user.name }}</span>
          <span class="tray-user-selector__email">{{ user.email }}</span>
        </span>
        <button
          type="button"
          class="tray-user-selector__remove"
          :aria-label="`Remove ${user.name}`"
          @click="handleRemoveUser(user.uuid)"
        >
          Remove
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.tray-user-selector {
  margin-top: var(--spacer-2);
}

.tray-user-selector__selected-list {
  list-style: none;
  margin: var(--spacer-2) 0 0;
  padding: 0;
  border-top: 1px solid var(--gsd-light-grey);
}

.tray-user-selector__selected-item {
  display: flex;
  justify-content: space-between;
  gap: var(--spacer-1);
  padding: var(--spacer-1) 0;
  border-bottom: 1px solid var(--gsd-light-grey);
}

.tray-user-selector__user {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.tray-user-selector__name {
  font-family: var(--font-sans);
  font-size: var(--typography-default-s-font-size);
  font-weight: 600;
}

.tray-user-selector__email {
  overflow-wrap: anywhere;
  color: var(--gsd-dark-grey-base);
  font-family: var(--font-sans);
  font-size: var(--typography-small-l-font-size);
}

.tray-user-selector__role-label {
  color: var(--gsd-dark-grey-base);
  font-family: var(--font-sans);
  font-size: var(--typography-small-l-font-size);
  font-weight: 400;
}

.tray-user-selector__remove {
  align-self: center;
  border: 1.5px solid var(--gsd-red-base, #c00);
  background: var(--gsd-white-base);
  color: var(--gsd-red-base, #c00);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--typography-small-l-font-size);
  padding: 4px 10px;
}
</style>
